/**
 * Category Parser
 * ================
 * Parse categories from CSV files.
 * 
 * The Zone4 CSV format uses FIRST COLUMN to indicate categories:
 * - Row 0: Headers (first cell empty)
 * - Category rows: First cell has category name (e.g., "Open Women 2x5km")
 * - Data rows: First cell is empty
 */

/**
 * Detect if CSV has categories (multi-category format).
 * @param {Array<Array>} rawRows - Raw CSV rows as arrays (not objects)
 * @returns {boolean}
 */
export function isMultiCategoryCSV(rawRows) {
  if (!rawRows || rawRows.length < 2) return false;
  
  // Standard templates have 'rank' in the first column header
  const firstCell = (rawRows[0]?.[0] || '').toLowerCase().trim();
  if (firstCell === 'rank') {
    return false; // Single-category template
  }
  
  // Check if any row (after header) has content in first column
  return rawRows.slice(1).some(row => {
    const firstCol = (row?.[0] || '').trim();
    return firstCol.length > 0;
  });
}

/**
 * Extract categories from raw CSV rows.
 * @param {Array<Array>} rawRows - Raw CSV rows as arrays
 * @returns {Array<{name: string, startIndex: number}>}
 */
export function extractCategories(rawRows) {
  const categories = [];
  
  rawRows.forEach((row, index) => {
    if (index === 0) return; // Skip header row
    
    // If first column has content, it's a category header
    const firstCol = (row?.[0] || '').trim();
    if (firstCol.length > 0) {
      categories.push({
        name: firstCol,
        startIndex: index,
      });
    }
  });
  
  return categories;
}

/**
 * Extract data for a specific category.
 * @param {Array<Array>} rawRows - Raw CSV rows
 * @param {number} categoryStartIndex - Index of the category row
 * @returns {Array<Object>} Data rows as objects with normalized headers
 */
export function extractCategoryData(rawRows, categoryStartIndex) {
  // Get headers from row 0
  let headers = [...(rawRows[0] || [])];
  
  // The category row may have additional headers (beyond main headers)
  const categoryRow = rawRows[categoryStartIndex];
  if (categoryRow) {
    // Extend headers if category row is longer
    while (headers.length < categoryRow.length) {
      headers.push('');
    }
    // Fill in headers from category row (skip first cell which is category name)
    for (let i = 1; i < categoryRow.length; i++) {
      const cellValue = (categoryRow[i] || '').trim();
      if (cellValue && (!headers[i] || headers[i].trim() === '')) {
        headers[i] = cellValue;
      }
    }
  }
  
  // Also check data rows in case they're longer than headers (common with lap times)
  if (rawRows.length > categoryStartIndex + 1) {
    const sampleDataRow = rawRows[categoryStartIndex + 1];
    if (sampleDataRow && headers.length < sampleDataRow.length) {
      headers = [...headers, ...Array(sampleDataRow.length - headers.length).fill('')];
    }
  }
  
  const data = [];
  let currentIndex = categoryStartIndex + 1;
  
  // Continue until next category or end of data
  while (currentIndex < rawRows.length) {
    const row = rawRows[currentIndex];
    
    // Stop if empty row
    if (!row || row.every(cell => !cell || cell.trim() === '')) {
      break;
    }
    
    // Stop if next category (first column has content)
    const firstCol = (row[0] || '').trim();
    if (firstCol.length > 0) {
      break;
    }
    
    // Convert row array to object
    const rowObj = {};
    headers.forEach((header, i) => {
      if (header && header.trim()) {
        rowObj[header.trim()] = (row[i] || '').trim();
      }
    });
    
    // Only add if row has meaningful data
    if (Object.values(rowObj).some(v => v && v.trim())) {
      data.push(rowObj);
    }
    
    currentIndex++;
  }
  
  return data;
}

/**
 * Get all unique headers from all category data.
 * @param {Array<Array>} rawRows - Raw CSV rows
 * @returns {Array<string>}
 */
export function getAllHeaders(rawRows) {
  const headerSet = new Set();
  
  // Add row 0 headers
  (rawRows[0] || []).forEach(h => {
    if (h && h.trim()) headerSet.add(h.trim());
  });
  
  // Check category rows for additional headers
  const categories = extractCategories(rawRows);
  categories.forEach(cat => {
    const categoryRow = rawRows[cat.startIndex];
    if (categoryRow) {
      categoryRow.forEach((cell, i) => {
        if (i > 0 && cell && cell.trim()) { // Skip first cell (category name)
          headerSet.add(cell.trim());
        }
      });
    }
  });
  
  return Array.from(headerSet);
}

/**
 * Determine race type from category name.
 * @param {string} category
 * @returns {'fis' | 'ussa' | 'both'}
 */
export function getCategoryRaceType(category) {
  if (!category) return 'both';
  
  // Youth categories (U16, U14, U12, U10) are typically USSA-only
  if (/^U(16|14|12|10|08|8)[:\s]/i.test(category)) {
    return 'ussa';
  }
  
  // Kinder categories are USSA-only
  if (/kinder/i.test(category)) {
    return 'ussa';
  }
  
  // Senior/Open/U18+/U20 typically have both
  if (/^(Open|Senior|U18\+|U20|U23)/i.test(category)) {
    return 'both';
  }
  
  // If contains distance like "10k" and age group, likely both
  if (/\d+k/i.test(category) && /(Men|Women|Male|Female)/i.test(category)) {
    return 'both';
  }
  
  return 'both';
}

const categoryParser = {
  isMultiCategoryCSV,
  extractCategories,
  extractCategoryData,
  getAllHeaders,
  getCategoryRaceType,
};

export default categoryParser;
